import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { SessionWhatsAppService } from '../session-whatsapp.service';

const invalidateSessionWhatsAppQueries = (
	queryClient: ReturnType<typeof useQueryClient>,
	sessionId?: string,
) => {
	queryClient.invalidateQueries({ queryKey: ['session-whatsapp'] });
	if (sessionId) {
		queryClient.invalidateQueries({ queryKey: ['session', sessionId] });
	}
};

export const useCreateSessionWhatsAppNotification = (
	onSuccess?: (
		notification: SessionWhatsApp.SessionWhatsAppNotification,
	) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: SessionWhatsApp.CreateBody) =>
			SessionWhatsAppService.create(body),
		onSuccess: (notification) => {
			invalidateSessionWhatsAppQueries(
				queryClient,
				notification.session_id,
			);
			toast.success('Notificação WhatsApp criada.');
			onSuccess?.(notification);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao criar notificação WhatsApp.'),
			);
		},
	});
};

export const useUpdateSessionWhatsAppNotification = (
	onSuccess?: (
		notification: SessionWhatsApp.SessionWhatsAppNotification,
	) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			id,
			body,
		}: {
			id: string;
			body: SessionWhatsApp.UpdateBody;
		}) => SessionWhatsAppService.patch(id, body),
		onSuccess: (notification) => {
			invalidateSessionWhatsAppQueries(
				queryClient,
				notification.session_id,
			);
			toast.success('Notificação WhatsApp atualizada.');
			onSuccess?.(notification);
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao atualizar notificação WhatsApp.',
				),
			);
		},
	});
};

export const useDeleteSessionWhatsAppNotification = (
	onSuccess?: () => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, sessionId }: { id: string; sessionId?: string }) =>
			SessionWhatsAppService.remove(id).then(() => sessionId),
		onSuccess: (sessionId) => {
			invalidateSessionWhatsAppQueries(queryClient, sessionId);
			toast.success('Notificação WhatsApp removida.');
			onSuccess?.();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(error, 'Erro ao remover notificação WhatsApp.'),
			);
		},
	});
};
